import { type BlogArticleCMS } from "@repo/common/src/types/firestore/cms/blogArticle";
import { CMSCollections } from "@repo/common/src/types/firestore/collections";
import { StoragePaths } from "@repo/common/src/types/storage/storagePaths";
import {
  type EntityReference,
  buildCollection,
  buildEntityCallbacks,
  buildProperty,
  type EntityOnSaveProps,
} from "@firecms/core";
import { getFirestore } from "firebase/firestore";

import { Groups } from "./types";
import { standardPermissions } from "../utils/constants";
import { generateUniqueSlug } from "../utils/generateUniqueSlug";
import { loadingOverlay } from "../utils/loadOverlay";

type BlogArticleCMSModel = Omit<BlogArticleCMS, "author"> & { author: EntityReference };

const callbacks = buildEntityCallbacks({
  onPreSave: async (props: EntityOnSaveProps<BlogArticleCMSModel>) => {
    loadingOverlay.show();
    const { values, entityId } = props;

    if (!values.title) {
      return values;
    }
    const baseSlug = values.slug || values.title;
    values.slug = await generateUniqueSlug(getFirestore(), CMSCollections.BlogArticles, baseSlug, entityId);

    return values;
  },
  onSaveSuccess: async () => {
    loadingOverlay.hide();
  },
  onSaveFailure: async () => {
    loadingOverlay.hide();
  },
});

export const blogArticlesCollection = buildCollection<BlogArticleCMSModel>({
  id: CMSCollections.BlogArticles,
  name: "Blogartikel",
  singularName: "Blogartikel",
  path: CMSCollections.BlogArticles,
  icon: "article",
  group: Groups.Content,
  permissions: () => {
    return standardPermissions;
  },
  callbacks,
  properties: {
    published: {
      name: "Veröffentlicht",
      dataType: "boolean",
      description: "Sichtbarkeit des Blog Posts",
    },
    date: {
      name: "Veröffentlichungsdatum",
      validation: { required: true },
      dataType: "date",
    },
    created_on: buildProperty({
      name: "Erstellungsdatum",
      dataType: "date",
      autoValue: "on_create",
    }),
    updated_on: buildProperty({
      name: "Aktualisierungsdatum",
      dataType: "date",
      autoValue: "on_update",
    }),
    title: {
      name: "Title",
      validation: { required: true },
      dataType: "string",
    },
    slug: {
      name: "URL-freundlicher Slug. Wird automatisch generiert, wenn leer.",
      dataType: "string",
    },
    title_seo: {
      name: "SEO Title",
      validation: { required: true, max: 60 },
      dataType: "string",
    },
    description: {
      name: "Description",
      multiline: true,
      dataType: "string",
      validation: { required: true },
      columnWidth: 300,
    },
    description_seo: {
      name: "SEO Description",
      multiline: true,
      dataType: "string",
      validation: { required: true, max: 160 },
      columnWidth: 300,
    },
    title_image: buildProperty({
      name: "Title Image. Bitte in 1024x576 hochladen.",
      dataType: "string",
      validation: { required: true },
      storage: {
        storagePath: StoragePaths.BlogArticlesImages,
        acceptedFiles: ["image/*"],
        maxSize: 1 * 1024 * 1024, // 1 MB in bytes
        imageCompression: {
          maxWidth: 1024,
          maxHeight: 576,
        },
      },
    }),
    download_title: {
      name: "Download Name",
      dataType: "string",
    },
    download_link: buildProperty({
      name: "Download Link",
      dataType: "string",
      storage: {
        storagePath: StoragePaths.BlogArticlesDownloads,
        acceptedFiles: ["application/pdf", "audio/mpeg"],
        maxSize: 100 * 1024 * 1024, // 100 MB in bytes
      },
    }),
    author: {
      dataType: "reference",
      name: "Author",
      validation: { required: true },
      path: CMSCollections.Authors,
    },
    text: {
      name: "Beschreibung",
      dataType: "string",
      validation: { required: true },
      markdown: true,
    },
  },
});
